/**
 * Modal de **confirmación** genérico del dominio de Inventario (p. ej. borrar un
 * lote). Recibe el estado de la mutación (pendiente/error) del padre, que es quien
 * dispara la acción; este componente solo confirma y muestra el error de la API si
 * lo hay (no pantalla en blanco). Controlado por `open` del padre. Mismo patrón
 * que `admin/ConfirmDeleteDialog`, pero local para mantener el dominio
 * autocontenido.
 */
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  /** Texto del botón de confirmación (default "Eliminar"). */
  confirmLabel?: string;
  /** Texto mientras la acción está en curso (default "Eliminando…"). */
  pendingLabel?: string;
  errorMessage?: string | null;
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Eliminar",
  pendingLabel = "Eliminando…",
  errorMessage,
  isPending,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>

        {errorMessage ? (
          <p role="alert" className="mt-4 text-sm text-red-600">
            {errorMessage}
          </p>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button type="button" variant="danger" onClick={onConfirm} disabled={isPending}>
            {isPending ? pendingLabel : confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
